import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { membershipsApi } from "../accounts/memberships";
import type { AccountRole } from "./accounts";

// ===== 型定義 =====

/**
 * `capacity_index` の値。標準値は 5 段階 (`one`〜`five`) だが、サーバ側で任意の
 * テキスト (最大 40 文字、例: 「今は手一杯」) も許容されるため `string` も受ける。
 */
export type CapacityIndex =
  | "one"
  | "two"
  | "three"
  | "four"
  | "five"
  | string;

/**
 * `upcoming_schedule` の各要素。
 *
 * - サーバ日付の今日 〜 今日 + 20 日 (21 日間) の範囲。
 * - `status` が `off` または `maybe` のエントリのみ返却される (`normal` は含まれない)。
 */
export interface UpcomingScheduleEntry {
  /** `YYYY-MM-DD` 形式の日付。 */
  date: string;
  status: "off" | "maybe";
}

/**
 * フォロー / フォロワー / ロール×ユーザー API で共通の最小ユーザー要素。
 * `/api/me` と同じソースで生成される共通シリアライザ (`UserSummarySerializer`) に
 * 準拠する。CREATOR ロール以外のユーザーや、Creator レコードが無いユーザーでは
 * `offer_criteria` / `capacity_index` / `capacity_index_updated_at` /
 * `schedule_overview` / `upcoming_schedule` がすべて `null` で返る。
 */
export interface FollowUser {
  id: number;
  display_name_jp: string;
  display_name_en: string;
  avatar_url: string | null;
  title_jp: string;
  title_en: string;
  affiliation_jp: string;
  affiliation_en: string;
  is_following: boolean;
  is_blocked: boolean;
  offer_criteria: string | null;
  capacity_index: CapacityIndex | null;
  capacity_index_updated_at: string | null;
  schedule_overview: string | null;
  upcoming_schedule: UpcomingScheduleEntry[] | null;
  /** 公開プロフィールの vanity_slug。公開中の CREATOR のみ。非公開・未設定・非 CREATOR は null。 */
  vanity_slug: string | null;
  /** アカウント種別。`/api/me` と同じ公開値（ADMIN / MANAGER / CREATOR / CLIENT）。 */
  role: AccountRole;
}

export interface PaginatedUsersData {
  users: FollowUser[];
  count: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export interface PaginatedUsersResponse {
  status: string;
  data: PaginatedUsersData;
}

export interface ListUsersParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export interface FollowMutationResponse {
  status: string;
  data?: {
    user_id: number;
    is_following: boolean;
    is_blocked?: boolean;
  };
  message?: string;
}

export interface BlockMutationResponse {
  status: string;
  data?: {
    user_id: number;
    is_blocked: boolean;
    is_following?: boolean;
  };
  message?: string;
}

const buildQueryString = ({ q, limit, offset }: ListUsersParams): string => {
  const params: Record<string, string> = {};
  if (q) params.q = q;
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : "";
};

// ===== API スライス =====

export const followsApi = createApi({
  reducerPath: "followsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Follows", "Followers", "Blocks", "FollowState"],
  endpoints: (builder) => ({
    // フォロー中一覧
    getFollows: builder.query<PaginatedUsersResponse, ListUsersParams>({
      query: (params) => `/api/me/follows${buildQueryString(params)}`,
      providesTags: ["Follows"],
    }),

    // フォロワー一覧
    getFollowers: builder.query<PaginatedUsersResponse, ListUsersParams>({
      query: (params) => `/api/me/followers${buildQueryString(params)}`,
      providesTags: ["Followers"],
    }),

    // ブロック中一覧
    getBlocks: builder.query<PaginatedUsersResponse, ListUsersParams>({
      query: (params) => `/api/me/blocks${buildQueryString(params)}`,
      providesTags: ["Blocks"],
    }),

    // フォロー
    // 一覧ページの楽観的更新で UI 状態を保持するため、ここでは Follows タグを
    // 無効化しない。タブ切替・検索条件変更・再マウントなど自然なリフレッシュ
    // タイミングで `getFollows` が再取得され、サーバー実データと同期する。
    // 一方、プロジェクトメンバー一覧（memberships）のレスポンスにも
    // `is_following` が含まれるため、こちらは無効化して再 fetch を促す。
    followUser: builder.mutation<FollowMutationResponse, number>({
      query: (userId) => ({
        url: `/api/me/follows/${userId}`,
        method: "POST",
      }),
      async onQueryStarted(_userId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(membershipsApi.util.invalidateTags(["MembershipsList"]));
        } catch {
          // 失敗時は何もしない
        }
      },
    }),

    // アンフォロー
    // followUser と同じ理由で Follows タグを無効化しない。OFF にした直後でも
    // 行をリストに残すことで OFF -> ON のやり直しを容易にする。
    unfollowUser: builder.mutation<FollowMutationResponse, number>({
      query: (userId) => ({
        url: `/api/me/follows/${userId}`,
        method: "DELETE",
      }),
      async onQueryStarted(_userId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(membershipsApi.util.invalidateTags(["MembershipsList"]));
        } catch {
          // 失敗時は何もしない
        }
      },
    }),

    // ブロック（フォロワー画面の「削除」もこちらに統合）
    // - 自分 -> 相手の follow を解除（自分が相手をフォロー中だった場合）
    // - 相手 -> 自分の follow はそのまま残す（相手は気付かない）
    // - `UserBlock` を作成
    // ブロック後も対象ユーザーは `GET /api/me/followers` のレスポンスに残り、
    // `is_blocked: true` で返るため、フロント側はリストから除去せず「削除中」
    // 表示に切り替えるだけで良い。
    // → Followers タグは無効化しない（累積リストを保持するため）。
    //   Follows と Blocks のキャッシュ、および memberships の `is_following` は
    //   実体が変わるため無効化する。
    blockUser: builder.mutation<BlockMutationResponse, number>({
      query: (userId) => ({
        url: `/api/me/blocks/${userId}`,
        method: "POST",
      }),
      invalidatesTags: ["Follows", "Blocks"],
      async onQueryStarted(_userId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(membershipsApi.util.invalidateTags(["MembershipsList"]));
        } catch {
          // 失敗時は何もしない
        }
      },
    }),

    // アンブロック
    unblockUser: builder.mutation<BlockMutationResponse, number>({
      query: (userId) => ({
        url: `/api/me/blocks/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Blocks"],
    }),
  }),
});

export const {
  useGetFollowsQuery,
  useGetFollowersQuery,
  useGetBlocksQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
} = followsApi;
